import { useMemo, useState, useEffect } from 'react';
import { format, isSameMonth } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar1 as CalendarIcon } from 'lucide-react';
import { Calendar as DatePicker } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDayPicker, type MonthCaptionProps } from 'react-day-picker';
import { useCalendarStore } from '@/stores/calendarStore';
import { useCalendarActionsStore } from '@/stores/calendarActionsStore';
import { useCalendarViewStore } from '@/stores/calendarViewStore';
import { parseDateOnly } from '@/lib/dateOnly';
import { CalendarVisibilitySelector } from './CalendarVisibilitySelector';
import { pickForeground } from './calendarColorUtils';

export function CalendarSidebarContent() {
  const calendars = useCalendarStore((state) => state.calendars);
  const calendarOrder = useCalendarStore((state) => state.calendarOrder);
  const useDefaultVisibleCalendars = useCalendarStore((state) => state.useDefaultVisibleCalendars);
  const rawVisibleCalendars = useCalendarStore((state) => state.visibleCalendarUids);
  const defaultCalendarUids = useCalendarStore((state) => state.defaultCalendarUids);
  const visibleCalendarUids = useDefaultVisibleCalendars ? defaultCalendarUids : rawVisibleCalendars;
  const toggleCalendarVisibility = useCalendarStore((state) => state.toggleCalendarVisibility);
  const setVisibleCalendars = useCalendarStore((state) => state.setVisibleCalendars);
  const focusDate = useCalendarViewStore((state) => state.focusDate);
  const setFocusDate = useCalendarViewStore((state) => state.setFocusDate);
  const refreshingCalendars = useCalendarActionsStore((state) => state.refreshingCalendars);
  const refreshCalendars = useCalendarActionsStore((state) => state.refreshCalendars);
  const updateCalendarColor = useCalendarActionsStore((state) => state.updateCalendarColor);
  const selectedDate = useMemo(() => parseDateOnly(focusDate), [focusDate]);
  const [displayMonth, setDisplayMonth] = useState(selectedDate);

  useEffect(() => {
    setDisplayMonth(selectedDate);
  }, [selectedDate]);

  const handleToday = () => {
    const today = new Date();
    setFocusDate(format(today, 'yyyy-MM-dd'));
    setDisplayMonth(today);
  };

  const handleColorSelect = (uid: string, color: string) => {
    return updateCalendarColor({ calendarUid: uid, backgroundColor: color, foregroundColor: pickForeground(color) });
  };

  const todayVisibleInFirstMonth = isSameMonth(displayMonth, new Date());

  return (
    <div className="px-2 py-4 space-y-4 sm:space-y-6">
      <div className="space-y-3">
        <DatePicker
          mode="single"
          selected={selectedDate}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          numberOfMonths={2}
          hideNavigation
          components={{
            MonthCaption: (props) => (
              <SidebarMonthCaption
                {...props}
                showTodayButton={!todayVisibleInFirstMonth}
                onToday={handleToday}
              />
            )
          }}
          onSelect={(date) => {
            if (date) {
              setFocusDate(format(date, 'yyyy-MM-dd'));
            }
          }}
          classNames={{
            root: "rounded-lg border shadow-sm",
            months: "flex flex-col gap-2",
            month: "space-y-1",
            month_caption: "text-sm font-medium text-center",
            today: "bg-secondary/60 rounded-full",
            outside: "text-muted-foreground opacity-50",
          }}

        />
      </div>
      <CalendarVisibilitySelector
        calendars={calendars}
        calendarOrder={calendarOrder}
        visibleCalendarUids={visibleCalendarUids}
        onToggleCalendar={toggleCalendarVisibility}
        onSetVisibleCalendars={setVisibleCalendars}
        onSelectCalendarColor={handleColorSelect}
        onRefreshCalendars={() => {
          void refreshCalendars();
        }}
        refreshingCalendars={refreshingCalendars}
      />
    </div>
  );
}

function SidebarMonthCaption({
  displayIndex,
  className,
  showTodayButton,
  onToday,
  calendarMonth,
  ...props
}: MonthCaptionProps & { showTodayButton: boolean; onToday: () => void }) {
  const { previousMonth, nextMonth, goToMonth } = useDayPicker();
  const showNav = displayIndex === 0;
  const showToday = showNav && showTodayButton;
  const monthLabel = format(calendarMonth.date, "MMM yy");

  const handleGoToMonth = (month?: Date) => {
    if (month) {
      goToMonth(month);
    }
  };

  return (
    <div
      {...props}
      className={cn('flex items-center justify-between gap-2', className)}
    >
      <div className="flex items-center justify-center h-8 px-1 text-base font-medium">
        {monthLabel}
      </div>
      {showToday ? (
        <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={onToday}>
          <CalendarIcon className="h-4 w-4" />
        </Button>
      ) : (
        <span aria-hidden="true" className="h-8 w-8" />
      )}
      {showNav ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => handleGoToMonth(previousMonth)}
          disabled={!previousMonth}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      ) : (
        <span aria-hidden="true" className="h-8 w-8" />
      )}
      {showNav ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={() => handleGoToMonth(nextMonth)}
          disabled={!nextMonth}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      ) : (
        <span aria-hidden="true" className="h-8 w-8" />
      )}
    </div>
  );
}
